import React, { useEffect, useMemo, useRef, useState } from 'react'
import styles from "./editor.module.css";
import { useTheme } from '../hooks/useTheme';

const enum EditorView {
    Preview,
    Code
};

/**
 * Props for the Editor component.
 * 
 * @param htmlCode - The HTML content to display in the editor.
 * @param setHTMLCode - Function to update the HTML content.
 * @param numOfLines - (Optional) The default number of lines for the editor.
 */
type EditorProps = {
    htmlCode: string;
    setHTMLCode: (value: string) => void;
    numOfLines?: number
}

const Editor = ({
    htmlCode,
    setHTMLCode,
    numOfLines = 10
}: EditorProps) => {
    const { themeStyles } = useTheme();
    const [viewType, setViewType] = useState<EditorView>(EditorView.Preview);

    const lineCount = useMemo(() => htmlCode?.split("\n")?.length, [htmlCode]);
    const linesArr = useMemo(
        () =>
            Array.from({ length: Math.max(numOfLines, lineCount) }, (_, i) => i + 1),
        [lineCount, numOfLines]
    );

    const lineCounterRef = useRef<HTMLDivElement | null>(null);
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;

            if (lineCounterRef.current) {
                lineCounterRef.current.style.height = 'auto';
                lineCounterRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
            }
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [htmlCode, viewType])

    useEffect(() => {

        const adjustIframeHeight = () => {
            if (iframeRef.current) {
                const iframeDocument = iframeRef.current?.contentDocument || iframeRef.current?.contentWindow?.document;
                if (iframeDocument) {
                    iframeRef.current.style.height = `${iframeDocument.documentElement.scrollHeight}px`;
                }
            }
        };

        if (iframeRef.current) {
            iframeRef.current.onload = adjustIframeHeight;
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewType]);


    const handleTextareaChange = (
        event: React.ChangeEvent<HTMLTextAreaElement>
    ) => {
        setHTMLCode(event.target.value);
    };

    const handleTextareaScroll = () => {
        if (lineCounterRef.current && textareaRef.current) {
            lineCounterRef.current.scrollTop = textareaRef.current.scrollTop;
        }
    };

    const ChangeTheView = (view: EditorView) => {
        setViewType(view);
    }

    return (
        <div className="w-full flex flex-col">
            <div className="w-full flex flex-row">
                <div
                    className={`flex justify-center items-center w-20 border text-xs font-semibold px-3 py-1 cursor-pointer ${viewType === EditorView.Preview
                        ? `!border-b-0 ${themeStyles?.sideBar?.tailwindMenuItemsColor}`
                        : `border-b ${themeStyles?.sideBar?.tailwindBorderColor}`
                        }`}
                    onClick={() => ChangeTheView(EditorView.Preview)}
                >
                    Code
                </div>
                <div
                    className={`flex justify-center items-center w-20 border text-xs font-semibold px-3 py-1 cursor-pointer ${viewType === EditorView.Code
                        ? `!border-b-0 ${themeStyles?.sideBar?.tailwindMenuItemsColor}`
                        : `border-b ${themeStyles?.sideBar?.tailwindBorderColor}`
                        }`}
                    onClick={() => ChangeTheView(EditorView.Code)}
                >
                    Preview
                </div>
            </div>

            <div className="mt-2">
                {viewType === EditorView.Preview ? (
                    <div className={`${styles.codeEditor}`}>
                        <div
                            ref={lineCounterRef}
                            className={`${styles.lineNumbers}`}
                            style={{ background: themeStyles?.sideBar?.bg }}
                        >
                            {linesArr.map((count) => (
                                <div
                                    key={count}
                                    className={`${styles.lineNumber}`}
                                    style={{ color: count <= lineCount ? "blue" : "inherit" }}
                                >
                                    {count}
                                </div>
                            ))}
                        </div>

                        <textarea
                            ref={textareaRef}
                            wrap="off"
                            className={`${styles.codeInput}`}
                            style={{
                                background: themeStyles?.editor?.bg,
                                color: themeStyles?.editor?.color
                            }}
                            value={htmlCode}
                            onChange={handleTextareaChange}
                            onScroll={handleTextareaScroll}
                        />
                    </div>
                ) : (
                    <div>
                        <iframe
                            ref={iframeRef}
                            title="Webview"
                            className="w-full h-auto"
                            style={{
                                background: themeStyles?.editor?.bg,
                                color: themeStyles?.editor?.color
                            }}
                            srcDoc={htmlCode}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}

export default Editor
